import type { DrinkEvent } from '../contracts/entities';

/**
 * Population parameters for a pragmatic ethanol PK model.
 *
 * Distribution uses the Forrest total-body-water equation when height is
 * available. The kinetic defaults are the central estimates of the venous
 * two-compartment model published by Buesker et al. (2023). Elimination uses a
 * neutral value between their fed and fasted estimates because the app
 * deliberately does not ask users about meals. The elimination estimate is
 * linear down to zero, avoiding an implausibly long low-level tail. Each logged
 * drink is consumed uniformly over 30 minutes, so a new drink does not enter
 * the body as one instantaneous dose.
 */
export const ALCOHOL_MODEL_DEFAULTS = {
  ethanolDensityGramsPerMl: 0.789,
  bloodWaterFraction: 0.825,
  fallbackDistributionFactor: 0.68,
  drinkDurationMinutes: 30,
  absorptionRatePerHour: Math.sqrt(1.45 * 3.64),
  eliminationGramsPerHour: Math.sqrt(6.31 * (6.31 * 1.39)),
  centralVolumeShare: 11.1 / (11.1 + 28.2),
  distributionClearancePerVolumePerHour: 96.8 / (11.1 + 28.2),
  integrationStepMinutes: 1,
  curveStepMinutes: 5,
} as const;

export interface AlcoholModelProfile {
  weightKg: number;
  heightCm?: number | null;
  distributionFactor?: number;
  eliminationRate?: number;
}

export interface AlcoholCurvePoint {
  at: string;
  estimatedPermille: number;
}

interface IngestionRateChange {
  at: number;
  gramsPerHour: number;
}

interface ModelState {
  gutGrams: number;
  centralGrams: number;
  peripheralGrams: number;
}

interface ModelParameters {
  centralVolumeLiters: number;
  peripheralVolumeLiters: number;
  absorptionRatePerHour: number;
  distributionClearanceLitersPerHour: number;
  eliminationGramsPerHour: number;
}

const ZERO_STATE: ModelState = { gutGrams: 0, centralGrams: 0, peripheralGrams: 0 };
const STATE_EPSILON_GRAMS = 1e-6;

export function pureAlcoholGrams(volumeMl: number, abv: number): number {
  return volumeMl * (abv / 100) * ALCOHOL_MODEL_DEFAULTS.ethanolDensityGramsPerMl;
}

/**
 * Estimates ethanol's apparent distribution volume from total body water.
 *
 * Forrest's male equation is used because the previous app model was likewise
 * calibrated to the conventional male Widmark factor of 0.68. A stored custom
 * factor still takes precedence. Profiles without height keep the previous
 * weight-only fallback and therefore need no additional user input.
 */
export function estimatedDistributionVolumeLiters(profile: AlcoholModelProfile): number {
  const { weightKg } = profile;
  if (profile.distributionFactor !== undefined) {
    return weightKg * profile.distributionFactor;
  }
  if (profile.heightCm === undefined || profile.heightCm === null) {
    return weightKg * ALCOHOL_MODEL_DEFAULTS.fallbackDistributionFactor;
  }

  const heightMeters = profile.heightCm / 100;
  const bodyMassIndex = weightKg / heightMeters ** 2;
  const estimatedFatFraction = (1.34 * bodyMassIndex - 12.467) / 100;
  const fatFreeMassKg = weightKg * (1 - estimatedFatFraction);
  const totalBodyWaterLiters = 0.724 * fatFreeMassKg;
  const apparentDistributionVolume =
    totalBodyWaterLiters / ALCOHOL_MODEL_DEFAULTS.bloodWaterFraction;

  // Keep extrapolations for unusual height/weight combinations physiological.
  return Math.min(weightKg * 0.85, Math.max(weightKg * 0.4, apparentDistributionVolume));
}

export function theoreticalContributionPermille(
  event: Pick<DrinkEvent, 'abv' | 'volumeMl'>,
  profile: AlcoholModelProfile,
): number {
  return pureAlcoholGrams(event.volumeMl, event.abv) / estimatedDistributionVolumeLiters(profile);
}

export function estimateAlcoholAt(
  events: ReadonlyArray<Pick<DrinkEvent, 'abv' | 'deletedAt' | 'occurredAt' | 'volumeMl'>>,
  profile: AlcoholModelProfile,
  at: Date,
): number {
  return estimateAlcoholAtTimes(events, profile, [at.getTime()])[0] ?? 0;
}

export function createAlcoholCurve(
  events: ReadonlyArray<Pick<DrinkEvent, 'abv' | 'deletedAt' | 'occurredAt' | 'volumeMl'>>,
  profile: AlcoholModelProfile,
  start: Date,
  end: Date,
  stepMinutes: number = ALCOHOL_MODEL_DEFAULTS.curveStepMinutes,
): AlcoholCurvePoint[] {
  if (end.getTime() < start.getTime() || stepMinutes <= 0) {
    return [];
  }

  const timestamps: number[] = [];
  const stepMs = stepMinutes * 60_000;
  for (let timestamp = start.getTime(); timestamp <= end.getTime(); timestamp += stepMs) {
    timestamps.push(timestamp);
  }
  const estimates = estimateAlcoholAtTimes(events, profile, timestamps);
  return timestamps.map((timestamp, index) => ({
    at: new Date(timestamp).toISOString(),
    estimatedPermille: estimates[index] ?? 0,
  }));
}

function estimateAlcoholAtTimes(
  events: ReadonlyArray<Pick<DrinkEvent, 'abv' | 'deletedAt' | 'occurredAt' | 'volumeMl'>>,
  profile: AlcoholModelProfile,
  timestamps: ReadonlyArray<number>,
): number[] {
  if (timestamps.length === 0) {
    return [];
  }

  const orderedSamples = timestamps
    .map((at, index) => ({ at, index }))
    .filter((sample) => Number.isFinite(sample.at))
    .sort((left, right) => left.at - right.at);
  const lastSampleAt = orderedSamples.at(-1)?.at;
  if (lastSampleAt === undefined) {
    return Array.from<number>({ length: timestamps.length }).fill(0);
  }

  const drinkDurationMs = ALCOHOL_MODEL_DEFAULTS.drinkDurationMinutes * 60_000;
  const drinkDurationHours = ALCOHOL_MODEL_DEFAULTS.drinkDurationMinutes / 60;
  const ingestionRateChanges = events
    .filter((event) => event.deletedAt === null)
    .flatMap((event): IngestionRateChange[] => {
      const startsAt = new Date(event.occurredAt).getTime();
      const grams = pureAlcoholGrams(event.volumeMl, event.abv);
      if (!Number.isFinite(startsAt) || startsAt > lastSampleAt || grams <= 0) {
        return [];
      }

      const gramsPerHour = grams / drinkDurationHours;
      return [
        { at: startsAt, gramsPerHour },
        { at: startsAt + drinkDurationMs, gramsPerHour: -gramsPerHour },
      ];
    })
    .sort((left, right) => left.at - right.at);
  const estimates = Array.from<number>({ length: timestamps.length }).fill(0);
  if (ingestionRateChanges.length === 0) {
    return estimates;
  }

  const distributionVolumeLiters = estimatedDistributionVolumeLiters(profile);
  const centralVolumeLiters = distributionVolumeLiters * ALCOHOL_MODEL_DEFAULTS.centralVolumeShare;
  const parameters: ModelParameters = {
    centralVolumeLiters,
    peripheralVolumeLiters: distributionVolumeLiters - centralVolumeLiters,
    absorptionRatePerHour: ALCOHOL_MODEL_DEFAULTS.absorptionRatePerHour,
    distributionClearanceLitersPerHour:
      distributionVolumeLiters * ALCOHOL_MODEL_DEFAULTS.distributionClearancePerVolumePerHour,
    eliminationGramsPerHour:
      profile.eliminationRate === undefined
        ? ALCOHOL_MODEL_DEFAULTS.eliminationGramsPerHour
        : profile.eliminationRate * distributionVolumeLiters,
  };

  let state = { ...ZERO_STATE };
  let currentAt = Math.min(
    orderedSamples[0]?.at ?? ingestionRateChanges[0]!.at,
    ingestionRateChanges[0]!.at,
  );
  let ingestionRateChangeIndex = 0;
  let ingestionGramsPerHour = 0;

  function applyIngestionRateChanges(): void {
    while (
      ingestionRateChangeIndex < ingestionRateChanges.length &&
      ingestionRateChanges[ingestionRateChangeIndex]!.at <= currentAt
    ) {
      ingestionGramsPerHour += ingestionRateChanges[ingestionRateChangeIndex]!.gramsPerHour;
      ingestionRateChangeIndex += 1;
    }
    if (Math.abs(ingestionGramsPerHour) <= STATE_EPSILON_GRAMS) {
      ingestionGramsPerHour = 0;
    }
  }

  function advanceTo(targetAt: number): void {
    if (targetAt <= currentAt) {
      return;
    }

    const maximumStepMs = ALCOHOL_MODEL_DEFAULTS.integrationStepMinutes * 60_000;
    applyIngestionRateChanges();
    while (currentAt < targetAt) {
      const nextRateChangeAt = ingestionRateChanges[ingestionRateChangeIndex]?.at ?? Infinity;
      if (totalGrams(state) <= STATE_EPSILON_GRAMS && ingestionGramsPerHour === 0) {
        state = { ...ZERO_STATE };
        currentAt = Math.min(targetAt, nextRateChangeAt);
        applyIngestionRateChanges();
        continue;
      }

      const stepEnd = Math.min(currentAt + maximumStepMs, targetAt, nextRateChangeAt);
      state = integrateRungeKutta(
        state,
        (stepEnd - currentAt) / 3_600_000,
        parameters,
        ingestionGramsPerHour,
      );
      currentAt = stepEnd;
      applyIngestionRateChanges();
    }
  }

  for (const sample of orderedSamples) {
    advanceTo(sample.at);
    estimates[sample.index] = normalized(state.centralGrams) / parameters.centralVolumeLiters;
  }

  return estimates;
}

function integrateRungeKutta(
  state: ModelState,
  elapsedHours: number,
  parameters: ModelParameters,
  ingestionGramsPerHour: number,
): ModelState {
  const k1 = derivative(state, parameters, ingestionGramsPerHour);
  const k2 = derivative(addScaled(state, k1, elapsedHours / 2), parameters, ingestionGramsPerHour);
  const k3 = derivative(addScaled(state, k2, elapsedHours / 2), parameters, ingestionGramsPerHour);
  const k4 = derivative(addScaled(state, k3, elapsedHours), parameters, ingestionGramsPerHour);
  const next = {
    gutGrams:
      state.gutGrams +
      (elapsedHours / 6) * (k1.gutGrams + 2 * k2.gutGrams + 2 * k3.gutGrams + k4.gutGrams),
    centralGrams:
      state.centralGrams +
      (elapsedHours / 6) *
        (k1.centralGrams + 2 * k2.centralGrams + 2 * k3.centralGrams + k4.centralGrams),
    peripheralGrams:
      state.peripheralGrams +
      (elapsedHours / 6) *
        (k1.peripheralGrams + 2 * k2.peripheralGrams + 2 * k3.peripheralGrams + k4.peripheralGrams),
  };

  if (totalGrams(next) <= STATE_EPSILON_GRAMS) {
    return { ...ZERO_STATE };
  }
  return {
    gutGrams: normalized(next.gutGrams),
    centralGrams: normalized(next.centralGrams),
    peripheralGrams: normalized(next.peripheralGrams),
  };
}

function derivative(
  state: ModelState,
  parameters: ModelParameters,
  ingestionGramsPerHour: number,
): ModelState {
  const gutGrams = normalized(state.gutGrams);
  const centralGrams = normalized(state.centralGrams);
  const peripheralGrams = normalized(state.peripheralGrams);
  const absorption = parameters.absorptionRatePerHour * gutGrams;
  const centralConcentration = centralGrams / parameters.centralVolumeLiters;
  const peripheralConcentration = peripheralGrams / parameters.peripheralVolumeLiters;
  const distribution =
    parameters.distributionClearanceLitersPerHour *
    (centralConcentration - peripheralConcentration);
  const distributedGrams = centralGrams + peripheralGrams;
  const elimination =
    distributedGrams > STATE_EPSILON_GRAMS ? parameters.eliminationGramsPerHour : 0;
  const centralElimination =
    distributedGrams > 0 ? elimination * (centralGrams / distributedGrams) : 0;
  const peripheralElimination =
    distributedGrams > 0 ? elimination * (peripheralGrams / distributedGrams) : 0;

  return {
    gutGrams: ingestionGramsPerHour - absorption,
    centralGrams: absorption - distribution - centralElimination,
    peripheralGrams: distribution - peripheralElimination,
  };
}

function addScaled(state: ModelState, change: ModelState, scale: number): ModelState {
  return {
    gutGrams: state.gutGrams + change.gutGrams * scale,
    centralGrams: state.centralGrams + change.centralGrams * scale,
    peripheralGrams: state.peripheralGrams + change.peripheralGrams * scale,
  };
}

function totalGrams(state: ModelState): number {
  return state.gutGrams + state.centralGrams + state.peripheralGrams;
}

function normalized(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}
